import { Capacitor } from '@capacitor/core';
import { useIonAlert } from '@ionic/react';
import BackgroundGeolocation, { Config, Location, MotionActivityType } from '@transistorsoft/capacitor-background-geolocation';
import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { enableGeolocationLogRef } from '../App';
import { logTransaction } from '../common/log';
import { destroyLocations, getLocation, getNativeGeocode, getPositionString } from '../common/mapsUtil';
import { IGeocodedAddress } from '../models/dms/address.model';
import { IGeolocation } from '../models/geolocation.model';
import { LogType } from '../models/log.model';
import store from '../store';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { resetDistanceTrackingDataStoreAsync, setAddressState, setAllowPermissionStoreAsync, setHasAskedPermissionStoreAsync, setLastPositionState } from '../store/locationSlice';
import { setDistanceTrackingUserActivatedStoreAsync, setDistanceTrackingUserPausedStoreAsync, setGpsServicesAvailableState, setGpsServicesUserEnabledStoreAsync, setGpsWatchStartedState, setGpsWatchStartingState } from '../store/settingsSlice';
import { setLabourClockingIsTrackingDistanceStoreAsync } from '../store/technicianClockingSlice';
import { useToast } from './ToastProvider';

interface IComponentProps {
  children: ReactNode;
}

const defaultBackgroundGeolocationConfig = {
  // Geolocation Config
  desiredAccuracy: BackgroundGeolocation.DESIRED_ACCURACY_HIGH,
  distanceFilter: 10,
  // Activity Recognition
  stopTimeout: 5,
  // Application config
  //debug: true,            // Enable this hear sounds for background-geolocation life-cycle
  logLevel: BackgroundGeolocation.LOG_LEVEL_OFF,
  stopOnTerminate: true, // Allow the background-service to continue tracking when user closes the app
  startOnBoot: false, // Auto start tracking when device is powered-up
  showsBackgroundLocationIndicator: true,

  logMaxDays: 1,

  locationAuthorizationRequest: 'Always',
  locationAuthorizationAlert: {
    titleWhenNotEnabled: 'Location Services not enabled',
    titleWhenOff: 'Location Services OFF',
    instructions: "You should enable 'Always' in Location Services",
    cancelButton: 'Cancel',
    settingsButton: 'Settings',
  },
  backgroundPermissionRationale: {
    title: "Allow {applicationName} to access this device's location in the background?",
    message: 'In order to track your activity in the background, please enable {backgroundPermissionOptionLabel} location permission',
    positiveAction: 'Change to {backgroundPermissionOptionLabel}',
    negativeAction: 'Cancel',
  },
  notification: {
    title: 'Location Tracking',
    text: 'Background location services activated',
    sticky: true,
  },
} as Config;

// Status
let hasInit = false;
let isInit = false;

const LocationProvider: React.FC<IComponentProps> = function ({ children }) {
  const { technician } = useAppSelector((state) => state.auth);
  const { hasAskedPermision, allowPermission, lastPosition } = useAppSelector((state) => state.location);
  const { distanceTrackingUserActivated, distanceTrackingUserPaused } = useAppSelector((state) => state.settings);
  const {
    actions: { errorToast },
  } = useToast();
  const dispatch = useAppDispatch();
  const [presentAlert] = useIonAlert();
  // lastActivity
  const [lastActivity, _setLastActivity] = useState<MotionActivityType>('unknown');
  const lastActivityRef = useRef(lastActivity);
  const setLastActivity = (data: MotionActivityType) => {
    lastActivityRef.current = data;
    _setLastActivity(data);
  };

  // Only start watching location when technician code is available
  useEffect(() => {
    if (technician?.code) {
      init(hasAskedPermision);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [technician?.code, hasAskedPermision]);

  // Run a simple timer that gets current position if position hasn't been detected in a while
  const locationDetectionThreshold = 30000;
  useEffect(() => {
    const t = setTimeout(() => {
      if (lastPosition && distanceTrackingUserActivated && !distanceTrackingUserPaused) {
        if (lastActivityRef.current !== 'still') {
          log(LogType.Warn, `Location has not been detected in at least ${locationDetectionThreshold}ms`, true);
          getCurrentPosition(true);
        }
      }
    }, locationDetectionThreshold);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPosition, distanceTrackingUserActivated, distanceTrackingUserPaused]);

  // Initialise location services
  const init = async (hasAskedPermision: boolean) => {
    if (!hasInit && !isInit) {
      try {
        isInit = true;
        log(LogType.Internal, `Init location services`, true);
        const { enableGeolocationLog } = store.getState().settings;
        await BackgroundGeolocation.ready({
          ...defaultBackgroundGeolocationConfig,
          logLevel: enableGeolocationLog ? BackgroundGeolocation.LOG_LEVEL_VERBOSE : BackgroundGeolocation.LOG_LEVEL_DEBUG,
        });

        let disclosureRes = false;

        if (hasAskedPermision) {
          if (allowPermission) {
            disclosureRes = true;
          } else {
            disclosureRes = false;
          }
        } else {
          const platform = Capacitor.getPlatform();
          if (platform === 'web') {
            disclosureRes = true;
          } else {
            logTransaction(LogType.Info, `Prompting prominent disclosure`);
            disclosureRes = await promptProminentDisclosure();
            await dispatch(setHasAskedPermissionStoreAsync(true));
            await dispatch(setAllowPermissionStoreAsync(disclosureRes));
            logTransaction(LogType.Info, `Prominent disclosure result was ${disclosureRes}`);
          }
        }

        if (disclosureRes) {
          logTransaction(LogType.Info, `Disclosure result was true, getting location services ready`);
          await enableLocationTracking();
        } else {
          disableLocationTracking();
        }

        hasInit = true;
      } catch (ex: any) {
        hasInit = false;
      } finally {
        isInit = false;
      }
    }
  };

  const promptProminentDisclosure = async () => {
    const alertPromise = new Promise<boolean>(async (res) => {
      await presentAlert({
        header: '',
        subHeader: '',
        message: 'Tech Mobile collects location data to enable distance tracking even when the app is closed or not in use.',
        buttons: [
          {
            text: 'Deny',
            role: 'cancel',
          },
          {
            text: 'Accept',
            role: 'confirm',
          },
        ],
        onDidDismiss: ({ detail }) => {
          if (detail.role === 'confirm') {
            res(true);
          } else {
            res(false);
          }
        },
      });
    });
    return alertPromise;
  };

  const enableLocationTracking = async () => {
    try {
      log(LogType.Info, `Enabling location tracking`, true);

      await dispatch(setGpsServicesUserEnabledStoreAsync(true));
      dispatch(setGpsWatchStartingState(true));

      await BackgroundGeolocation.stop();
      await BackgroundGeolocation.removeListeners();

      log(LogType.Info, `Adding onLocation event`, true);
      BackgroundGeolocation.onLocation(
        (location) => {
          const { gpsServicesAvailable, gpsWatchStarting } = store.getState().settings;

          !gpsServicesAvailable && dispatch(setGpsServicesAvailableState(true));
          gpsWatchStarting && dispatch(setGpsWatchStartingState(false)) && dispatch(setGpsWatchStartedState(true));

          if (!location.sample) {
            handleLocation(location);
          }
        },
        async (error) => {
          log(LogType.Warn, `Location error: ${error}`, true);
          switch (error) {
            case 1:
              // User denied
              const msg = `User denied location services`;
              log(LogType.Warn, msg, true);
              errorToast(msg);
              await disableLocationTracking();
              break;
            case 2:
              // Network error
              log(LogType.Warn, `Location network error`, true);
              break;
            case 408:
              // Timeout
              log(LogType.Warn, `Location timeout`, true);
              break;
            case 499:
              // Location request cancelled
              log(LogType.Warn, `Location request cancelled`, true);
              break;
          }
        },
      );

      log(LogType.Info, `Adding onActivityChange event`, true);
      BackgroundGeolocation.onActivityChange((event) => {
        if (event.activity !== lastActivityRef.current) {
          setLastActivity(event.activity);
          log(LogType.Info, `Activity ${event.activity}`, true);
        }
      });

      log(LogType.Info, `Adding onPowerSaveChange event`, true);
      BackgroundGeolocation.onPowerSaveChange((isPowerSaveMode) => {
        log(LogType.Info, `onPowerSaveChange: "${isPowerSaveMode ? 'true' : 'false'}"`, true);
      });

      log(LogType.Info, `Enabling location + geofence tracking`, true);
      await BackgroundGeolocation.start(
        (state) => {
          log(LogType.Info, `Background geolocation services started`, true);
        },
        (error) => {
          log(LogType.Error, `Error starting background geolocation services. ${error}`, true);
        },
      );
    } catch (ex: any) {
      errorToast(`Error starting location services. ${ex.message}`);
      await disableLocationTracking();
    }
  };

  const disableLocationTracking = async () => {
    log(LogType.Info, `Disabling location tracking`, true);
    await dispatch(setGpsServicesUserEnabledStoreAsync(false));
    dispatch(setGpsServicesAvailableState(false));
    dispatch(setGpsWatchStartingState(false));
    dispatch(setGpsWatchStartedState(false));

    await BackgroundGeolocation.stop();
    await BackgroundGeolocation.removeListeners();
  };

  const pauseLocationTracking = async () => {
    log(LogType.Info, `Pausing location tracking`, true);
    dispatch(setGpsWatchStartingState(false));
    dispatch(setGpsWatchStartedState(false));

    await BackgroundGeolocation.stop();
  };

  const resumeLocationTracking = async () => {
    log(LogType.Info, `Resuming location tracking`, true);
    const { gpsWatchStarting, gpsWatchStarted } = store.getState().settings;

    if (gpsWatchStarting) {
      log(LogType.Warn, `Cannot resume location tracking as it is already starting`, true);
      return;
    }

    if (gpsWatchStarted) {
      log(LogType.Warn, `Cannot resume location tracking as it has already started`, true);
      return;
    }

    dispatch(setGpsWatchStartingState(true));
    await BackgroundGeolocation.start();
  };

  const startDistanceTracking = async () => {
    log(LogType.Info, `Staring distance tracking`, true);
    await BackgroundGeolocation.resetOdometer();
    await destroyLocations();
    await dispatch(setLabourClockingIsTrackingDistanceStoreAsync(true));
    await dispatch(resetDistanceTrackingDataStoreAsync());
    await getCurrentPosition(true);
    await dispatch(setDistanceTrackingUserActivatedStoreAsync(true));
    await dispatch(setDistanceTrackingUserPausedStoreAsync(false));
  };

  const stopDistanceTracking = async () => {
    log(LogType.Info, `Stopping distance tracking`, true);
    await dispatch(setLabourClockingIsTrackingDistanceStoreAsync(false));
    await dispatch(resetDistanceTrackingDataStoreAsync());
    await dispatch(setDistanceTrackingUserActivatedStoreAsync(false));
    await dispatch(setDistanceTrackingUserPausedStoreAsync(false));
  };

  const pauseDistanceTracking = async () => {
    log(LogType.Info, `Pausing distance tracking`, true);
    await dispatch(setDistanceTrackingUserPausedStoreAsync(true));
  };

  const resumeDistanceTracking = async () => {
    log(LogType.Info, `Resuming distance tracking`, true);
    await dispatch(setDistanceTrackingUserPausedStoreAsync(false));
  };

  const handleLocation = async (location: Location, force?: boolean) => {
    const thisPositionString = getPositionString(location);
    log(LogType.Info, `LOCAT |${thisPositionString}`);

    const okLocation = getLocation(location, true, false);
    if (okLocation) {
      dispatch(setLastPositionState(okLocation));
    }

    const { distanceTrackingUserActivated, distanceTrackingUserPaused } = store.getState().settings;

    if ((distanceTrackingUserActivated && !distanceTrackingUserPaused) || force) {
      // If started and not paused (or forced)
      const clensedLocation = getLocation(location, false, false);
      if (clensedLocation) {
        try {
          const addressData = await getGeocodedAddressData(clensedLocation);
          await dispatch(setAddressState(addressData));
        } catch (ex: any) {
          log(LogType.Error, `ADDRES|Failed to capture address. ${ex?.message || ex}`);
        }
      } else {
        log(LogType.Info, `Location criteria not met, destroying location`, true);
        await destroyLocation(location);
      }
    } else if (distanceTrackingUserActivated && distanceTrackingUserPaused) {
      // If started and paused
      log(LogType.Info, `Route tracking paused, destroying location`, true);
      await destroyLocation(location);
    } else if (!distanceTrackingUserActivated) {
      // If not started
      log(LogType.Info, `Route tracking not started, destroying location`, true);
      await destroyLocation(location);
    } else {
      // Another state
      log(LogType.Warn, `Route tracking state - routeTrackingStarted:${distanceTrackingUserActivated ? 'true' : 'false'}, routeTrackingPaused:${distanceTrackingUserPaused ? 'true' : 'false'}`, true);
    }
  };

  const getGeocodedAddressData = async (location: IGeolocation) => {
    // Try to get address natively
    let addressData = {} as IGeocodedAddress;

    try {
      addressData = await getNativeGeocode({ latitude: location.latitude, longitude: location.longitude } as IGeolocation);
    } catch {
      if (store.getState().app.isDebug) {
        // Only allow api geocode in debug mode
        // const geocode = await getApiGeocode({ latitude: location.latitude, longitude: location.longitude } as IGeolocation);
        // const routeResult = geocode.find(g => {
        //   return g.types.includes('route');
        // });
        // const streetAddressResult = geocode.find(g => {
        //   return g.types.includes('street_address');
        // });
        // const addressLocality = routeResult?.address_components?.find(g => g.types.includes('locality'))?.short_name;
        // const streetName = streetAddressResult?.address_components?.find(g => g.types.includes('route'))?.short_name;
        // addressData = {
        //   city: addressLocality || '',
        //   streetName: streetName || '',
        // };
      } else {
        // If native geocode failed and not debug mode, set address to last known address or empty
        addressData = store.getState().location.address || ({} as IGeocodedAddress);
      }
    }

    return addressData;
  };

  const getCurrentPosition = async (persist: boolean = false) => {
    try {
      log(LogType.Info, `Current position requested`, true);
      const position = await BackgroundGeolocation.getCurrentPosition({ desiredAccuracy: BackgroundGeolocation.DESIRED_ACCURACY_HIGH, persist });
      const lastLocation = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
      } as IGeolocation;
      log(LogType.Info, `Current position found using BackgroundGeolocation`, true);
      await handleLocation(position, persist);
      return lastLocation;
    } catch (ex: any) {
      log(LogType.Error, `Failed to capture BackgroundGeolocation.getCurrentPosition. ${ex.message}`, true);
      const platform = Capacitor.getPlatform();
      if (platform === 'web') {
        try {
          navigator.geolocation.getCurrentPosition((position) => {
            const lastLocation = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
            } as IGeolocation;
            log(LogType.Info, `Current position found using browser Navigator`, true);
            return lastLocation;
          });
        } catch (ex: any) {
          log(LogType.Error, `Failed to get current position. ${ex.message}`, true);
        }
      }
    }
    return undefined;
  };

  const destroyLocation = async (location: Location) => {
    try {
      await BackgroundGeolocation.destroyLocation(location.uuid);
    } catch (ex: any) {
      log(LogType.Error, `Failed to destroy location. ${ex.message}`, true);
    }
  };

  const log = (type: LogType, message: string, force: boolean = false) => {
    (enableGeolocationLogRef || force) && logTransaction(type, message);
  };

  return (
    <LocationContext.Provider
      value={{
        state: {},
        actions: {
          enableLocationTracking,
          disableLocationTracking,
          pauseLocationTracking,
          resumeLocationTracking,
          startDistanceTracking,
          stopDistanceTracking,
          resumeDistanceTracking,
          pauseDistanceTracking,
          getGeocodedAddressData,
          handleLocation,
        },
      }}>
      {children}
    </LocationContext.Provider>
  );
};

export const locationProvider = {
  state: {} as {},
  actions: {} as {
    enableLocationTracking: () => Promise<void>;
    disableLocationTracking: () => Promise<void>;
    pauseLocationTracking: () => Promise<void>;
    resumeLocationTracking: () => Promise<void>;
    startDistanceTracking: () => Promise<void>;
    stopDistanceTracking: () => Promise<void>;
    pauseDistanceTracking: () => Promise<void>;
    resumeDistanceTracking: () => Promise<void>;
    getGeocodedAddressData: (location: IGeolocation) => Promise<IGeocodedAddress>;
    handleLocation: (location: Location, isSimulation?: boolean) => Promise<void>;
  },
};

export const LocationContext = createContext<typeof locationProvider>(locationProvider);
export const useLocation = () => useContext(LocationContext);
export default LocationProvider;
